// --- React ---
import React, { useCallback, useEffect, useMemo } from 'react';

// --- 다국어 ---
import { useTranslation } from 'react-i18next';

// --- 연결: BLE/시리얼, 기기 정보 갱신·저장·기본값 로드 ---
import { useConnectionStore } from '@/store/connectionStore';
import { useConnectionActions } from '@/connections/hooks/useConnectionActions';

// --- 전역 UI 스토어 (토스트) ---
import { useNotificationStore } from '../../store/notificationStore';

// --- 공통 UI 컴포넌트 ---
import { GeneralConfigCard, SensorConfigCard } from '@/components/ui';
import { ConfigPageShell } from '@/components/configuration/ConfigPageShell';

// --- 공유 타입: LITE 기기 · 제약 조건 (@packages/types) ---
import { DeviceType } from '@packages/types/device/common/deviceTypes';
import type { DeviceConfiguration } from '@packages/types/device/lite/deviceConfiguration';
import type { DeviceInfo } from '@packages/types/device/lite/deviceInfo';

// --- 목업: LITE 스켈레톤 · 센서별 기본 설정 생성 (API가 기본값을 주기 전까지) ---
import {
  createLiteDefaultConfigForSensors,
  LITE_SKELETON_CONFIG_VALUES,
  LITE_SKELETON_DEVICE_INFO,
} from '@/mock-data/configuratorMockData';

// --- 이 페이지 전용: 검증 타입 · 상태 훅 ---
import type { FieldError } from './types/validation';
import { useConfigValidation } from './hooks/useConfigValidation';
import { useValidationState } from './hooks/useValidationState';
import { useConfigPageState } from './hooks/useConfigPageState';
import { useConfigPageController } from './hooks/useConfigPageController';

// --- 센서 데이터 헬퍼 · 설정 파일 헬퍼 ---
import {
  extractLiteSensorData,
  buildLiteSensorUpdate,
  getLiteSensorConstraint,
  getLiteSensorDecimalPoint,
  hasActiveLiteCo2Sensor,
} from './utils/sensorDataHelpers';
import { validateConfigFile, buildCradleMismatchMessage } from './utils/configFileHelpers';

// --- i18n 키 ---
import { i18nKeys } from '@/i18n/i18nKeyGen';

// --- 설정 화면 전용 레이아웃 스타일 ---
import {
  SensorGrid1,
  SensorGrid2,
  SensorGrid3,
  SensorGrid4,
  SensorGrid5,
} from './ConfigurationStyles';

/** 이 화면은 BELL LITE 기기 전용입니다. */
const EXPECTED_DEVICE_TYPE = DeviceType.LITE;

type SensorOption = { value: number; label: string };

const ConfigurationLite: React.FC = () => {
  const { t } = useTranslation('config');

  // ===== 스토어 접근 =====

  const { device } = useConnectionStore();
  const { showSuccess, showError } = useNotificationStore();

  // ===== 기기·설정 상태 =====

  const {
    detectedDeviceType,
    setDetectedDeviceType,
    curDevice,
    setCurDevice,
    deviceConfigData,
    setDeviceConfigData,
    editingCard,
    setEditingCard,
    configDeviceSerial,
    setConfigDeviceSerial,
    resetState,
  } = useConfigPageState<DeviceInfo, DeviceConfiguration>(
    LITE_SKELETON_DEVICE_INFO as unknown as DeviceInfo,
    LITE_SKELETON_CONFIG_VALUES as unknown as DeviceConfiguration
  );

  // ===== 파생 상태 (Derived) =====

  const isCompatible = useMemo(
    () => detectedDeviceType === EXPECTED_DEVICE_TYPE,
    [detectedDeviceType]
  );

  const isDeviceConnected = useMemo(() => device.type !== 'none', [device.type]);

  // 설정 데이터와 디바이스의 연결 보장 (시리얼 일치)
  const isConfigDataLoaded = useMemo(
    () =>
      !!configDeviceSerial &&
      !!curDevice?.serial_number &&
      configDeviceSerial === curDevice.serial_number,
    [configDeviceSerial, curDevice?.serial_number]
  );

  // 실제 장착된 센서 개수 (value가 255가 아닌 센서들)
  const actualSensorCount = useMemo(() => {
    const equippedSensors = (curDevice?.sensor_type as SensorOption[]) || [];
    return equippedSensors.filter(
      sensor => sensor && sensor.value !== undefined && sensor.value !== 255
    ).length;
  }, [curDevice?.sensor_type]);

  // 활성화된 센서 중 CO2 센서 존재 여부 (2nd Span Calibration 노출/강제 기준)
  const hasActiveCo2Sensor = useMemo(
    () => hasActiveLiteCo2Sensor(deviceConfigData as unknown as Record<string, unknown>),
    [deviceConfigData]
  );

  // ===== 유효성 검증 =====

  const {
    validationState,
    hintsState,
    setValidationResult,
    hasErrors: hasValidationErrors,
  } = useValidationState(DeviceType.LITE);

  const { validateAll } = useConfigValidation(DeviceType.LITE);

  const hasErrors = useCallback(
    () => hasValidationErrors(actualSensorCount),
    [hasValidationErrors, actualSensorCount]
  );

  // ===== 연결 콜백 =====

  const onDeviceLoaded = useCallback(
    (deviceInfo: any, deviceConfig: any) => {
      if ('sensor_type' in deviceInfo) {
        setDetectedDeviceType(DeviceType.LITE);
        setCurDevice(deviceInfo as DeviceInfo);
        setDeviceConfigData(deviceConfig as DeviceConfiguration);
        setConfigDeviceSerial(deviceInfo.serial_number);
      } else if ('equipped_sensor' in deviceInfo) {
        setDetectedDeviceType(DeviceType.MINI);
      }
    },
    [setCurDevice, setDeviceConfigData, setConfigDeviceSerial, setDetectedDeviceType]
  );

  const onDisconnected = useCallback(() => resetState(), [resetState]);

  const onConnectionChange = useCallback(() => {}, []);

  const { refreshDeviceInfo, saveDeviceConfig, loadDefaultConfig } = useConnectionActions(
    onDeviceLoaded,
    onConnectionChange,
    onDisconnected,
    true
  );

  // ===== 유효성 검증 트리거 =====

  useEffect(() => {
    if (deviceConfigData.timestamp) {
      const result = validateAll(deviceConfigData as unknown as Record<string, unknown>, curDevice);
      setValidationResult(result);
    }
  }, [deviceConfigData, curDevice, validateAll, setValidationResult]);

  // 활성화된 CO2 센서가 없으면 2nd Span Calibration은 항상 off로 강제
  useEffect(() => {
    if (!hasActiveCo2Sensor) {
      setDeviceConfigData(prev => {
        if (!prev || !prev.span_cal_and_bump) return prev;
        return { ...prev, span_cal_and_bump: false } as DeviceConfiguration;
      });
    }
  }, [hasActiveCo2Sensor, setDeviceConfigData]);

  // ===== 버튼 활성화 조건 =====

  const canSaveOrLoad =
    isDeviceConnected &&
    isCompatible &&
    isConfigDataLoaded &&
    !!configDeviceSerial &&
    !!curDevice?.serial_number &&
    configDeviceSerial === curDevice.serial_number;

  // ===== 저장 후 refresh 데이터 반영 =====

  const onSaved = useCallback(
    (deviceInfo: unknown, deviceConfig: unknown) => {
      setCurDevice(deviceInfo as DeviceInfo);
      setDeviceConfigData(deviceConfig as DeviceConfiguration);
      if ((deviceInfo as DeviceInfo)?.serial_number) {
        setConfigDeviceSerial((deviceInfo as DeviceInfo).serial_number);
      }
    },
    [setCurDevice, setDeviceConfigData, setConfigDeviceSerial]
  );

  // ===== 공용 컨트롤러 (저장/로드/스토리지/모달) =====

  const {
    showConfigListModal,
    setShowConfigListModal,
    showNameInputModal,
    setShowNameInputModal,
    handleSaveToStorage,
    handleSaveWithName,
    handleLoadFromStorage,
    handleSave,
  } = useConfigPageController({
    deviceType: DeviceType.LITE,
    curDevice,
    deviceConfigData: deviceConfigData as unknown as Record<string, unknown>,
    hasErrors,
    canSaveToDevice: canSaveOrLoad,
    saveDeviceConfig,
    refreshDeviceInfo,
    // 기기 시계는 "저장 순간의 현재 시각(UTC)"으로 맞춘다. (기기는 tz 없이 UTC로 보관)
    // set_date_time=1 이어야 기기가 timestamp로 RTC를 갱신한다. 항상 1로 전송.
    buildConfigToSend: data => ({
      ...data,
      timestamp: new Date().toISOString(),
      set_date_time: 1,
    }),
    onSaved,
  });

  // ===== 저장된 설정 적용 (LITE 멀티센서 호환성 검증) =====

  const handleConfigSelect = async (savedConfig: any) => {
    try {
      const configData = savedConfig.configData;

      const validation = validateConfigFile(configData, DeviceType.LITE, t);
      if (!validation.isValid) {
        showError(
          i18nKeys.config.msg_fileValidationFailed,
          validation.error || i18nKeys.config.msg_unknownError
        );
        return;
      }

      if (!isDeviceConnected || !isCompatible) {
        showError(i18nKeys.config.msg_cradleConnectFailed, buildCradleMismatchMessage(t));
        return;
      }

      const refreshResult = await refreshDeviceInfo();
      if (!refreshResult.success || !refreshResult.data?.deviceInfo) {
        showError(i18nKeys.config.msg_cradleConnectFailed, buildCradleMismatchMessage(t));
        return;
      }

      const currentDeviceInfo = refreshResult.data.deviceInfo as unknown as DeviceInfo;
      setCurDevice(currentDeviceInfo);

      if (!currentDeviceInfo.sensor_type) {
        showError(i18nKeys.config.msg_cradleConnectFailed, buildCradleMismatchMessage(t));
        return;
      }

      // 센서 호환성 검증 (LITE 멀티센서: 개수 + 타입 일치 확인)
      if (configData.deviceConfig?.sensor_type && currentDeviceInfo?.sensor_type) {
        const fileSensors = (configData.deviceConfig.sensor_type as SensorOption[]) || [];
        const currentSensors = currentDeviceInfo.sensor_type as SensorOption[];

        const fileActualSensors = fileSensors.filter(sensor => sensor.value !== 255);
        const currentActualSensors = currentSensors.filter(sensor => sensor.value !== 255);

        if (fileActualSensors.length !== currentActualSensors.length) {
          showError(
            i18nKeys.config.error_sensorCountMismatch,
            t(i18nKeys.config.error_sensorCountMismatchDesc, {
              fileCount: fileActualSensors.length,
              deviceCount: currentActualSensors.length,
            })
          );
          return;
        }

        const fileSensorTypes = fileActualSensors.map(s => s.value).sort();
        const currentSensorTypes = currentActualSensors.map(s => s.value).sort();

        if (fileSensorTypes.some((type, i) => type !== currentSensorTypes[i])) {
          showError(
            i18nKeys.config.error_sensorTypeMismatch,
            i18nKeys.config.error_sensorTypeMismatchDesc
          );
          return;
        }
      }

      if (configData.deviceConfig) {
        setDeviceConfigData(configData.deviceConfig as unknown as DeviceConfiguration);
        if (currentDeviceInfo?.serial_number) {
          setConfigDeviceSerial(currentDeviceInfo.serial_number);
        }
      }

      showSuccess(i18nKeys.config.msg_configLoaded, i18nKeys.config.msg_configLoaded);
    } catch {
      showError(i18nKeys.config.msg_configLoadFailed, i18nKeys.config.msg_configLoadFailed);
    }
  };

  // ===== 기본값 로드 =====

  const loadDefaultConfiguration = async () => {
    try {
      const result = await loadDefaultConfig();
      if (!result.success || !result.data?.deviceInfo) {
        showError(
          i18nKeys.config.msg_defaultValueLoadingFailed,
          i18nKeys.config.msg_defaultValueLoadingFailedDesc
        );
        return;
      }

      const deviceInfo = result.data.deviceInfo as unknown as DeviceInfo;
      const currentEquippedSensors = (deviceInfo.sensor_type as SensorOption[]) || [];
      setCurDevice(deviceInfo);

      const defaultConfig = createLiteDefaultConfigForSensors(currentEquippedSensors);

      // 소수점 자릿수에 맞게 반올림
      if (
        curDevice?.decimal_point &&
        curDevice?.unit &&
        Array.isArray(curDevice.decimal_point) &&
        Array.isArray(curDevice.unit)
      ) {
        const fieldsToRound = [
          'calibration_gas_conc',
          'low_alarm',
          'high_alarm',
          'twa_alarm',
          'stel_alarm',
        ];
        fieldsToRound.forEach(field => {
          if (Array.isArray(defaultConfig[field])) {
            const arrayValue = defaultConfig[field] as number[];
            defaultConfig[field] = arrayValue.map((value, index) => {
              if (
                typeof value === 'number' &&
                curDevice?.decimal_point &&
                Array.isArray(curDevice.decimal_point)
              ) {
                const decimalPt = curDevice.decimal_point[index] || 0;
                const multiplier = Math.pow(10, decimalPt);
                return Math.round(value * multiplier) / multiplier;
              }
              return value;
            });
          }
        });
      }

      defaultConfig.timestamp = new Date().toISOString();
      setDeviceConfigData(defaultConfig as unknown as DeviceConfiguration);

      if (curDevice?.serial_number) {
        setConfigDeviceSerial(curDevice.serial_number);
      }

      showSuccess(
        i18nKeys.config.msg_defaultValueLoading,
        t(i18nKeys.config.msg_defaultValueLoadingDesc, { count: currentEquippedSensors.length })
      );
    } catch {
      showError(
        i18nKeys.config.msg_defaultValueLoadingFailed,
        i18nKeys.config.msg_defaultValueLoadingFailedDesc
      );
    }
  };

  // ===== 설정 업데이트 핸들러 =====

  const handleGeneralConfigUpdate = useCallback(
    (updated: Record<string, unknown>) => {
      setDeviceConfigData(prev => ({ ...prev, ...updated }) as DeviceConfiguration);
    },
    [setDeviceConfigData]
  );

  const handleSensorConfigUpdate = useCallback(
    (index: number, updated: Record<string, unknown>) => {
      setDeviceConfigData(prev => buildLiteSensorUpdate(prev, index, updated));
    },
    [setDeviceConfigData]
  );

  // ===== 센서 카드 렌더링 =====

  const renderSensorCards = () => {
    if (!curDevice || !deviceConfigData.timestamp) {
      return [];
    }

    const equippedSensors = (curDevice?.sensor_type as SensorOption[]) || [];
    const configSensors = (deviceConfigData.sensor_type as SensorOption[]) || [];
    const actualEquippedSensors = equippedSensors.filter(sensor => sensor.value !== 255);
    const configAsRecord = deviceConfigData as unknown as Record<string, unknown>;

    return actualEquippedSensors.map(equippedSensor => {
      const originalIndex = equippedSensors.indexOf(equippedSensor);
      const sensorData = extractLiteSensorData(
        configAsRecord,
        equippedSensor,
        configSensors[originalIndex],
        originalIndex
      );

      const gasConUnit = sensorData.gas_conc_unit as SensorOption;
      const sensorValue = (sensorData.type as SensorOption).value as number;

      return (
        <SensorConfigCard
          key={originalIndex}
          sensorData={sensorData}
          originalData={sensorData}
          constraint={getLiteSensorConstraint(sensorValue, gasConUnit)}
          isEdit={
            isDeviceConnected && isCompatible && isConfigDataLoaded && editingCard === originalIndex
          }
          isSkeleton={false}
          deviceType={DeviceType.LITE}
          isClickable={isDeviceConnected && isCompatible && isConfigDataLoaded}
          onClick={() => {
            if (!isDeviceConnected || !isCompatible || !isConfigDataLoaded) return;
            setEditingCard(originalIndex);
          }}
          onUpdate={updated => handleSensorConfigUpdate(originalIndex, updated)}
          errors={Object.fromEntries(
            Object.entries(
              (validationState.sensors as Record<number, Record<string, FieldError>>)[
                originalIndex
              ] || {}
            ).map(([key, error]) => [key, error.message])
          )}
          hints={
            (hintsState.sensors as Record<number, Record<string, string>>)[originalIndex] || {}
          }
          calibrationDueDate={undefined}
          calibrationInterval={(deviceConfigData?.calibration_interval as number[])?.[originalIndex]}
          decimalPoint={getLiteSensorDecimalPoint(
            sensorValue,
            curDevice?.decimal_point as number[],
            originalIndex,
            gasConUnit
          )}
        />
      );
    });
  };

  // ===== 그리드 레이아웃 렌더링 =====

  const renderSensorGrid = () => {
    const sensorCount = Math.min(actualSensorCount, 5);
    const GridComponent =
      (
        {
          1: SensorGrid1,
          2: SensorGrid2,
          3: SensorGrid3,
          4: SensorGrid4,
          5: SensorGrid5,
        } as Record<number, React.ComponentType<any>>
      )[sensorCount] || SensorGrid1;

    return (
      <GridComponent>
        <GeneralConfigCard
          configData={deviceConfigData as unknown as Record<string, unknown>}
          isEdit={isDeviceConnected && isCompatible && isConfigDataLoaded && editingCard === null}
          isSkeleton={false}
          deviceType={DeviceType.LITE}
          isClickable={isDeviceConnected && isCompatible && isConfigDataLoaded}
          onClick={() => {
            if (!isDeviceConnected || !isCompatible || !isConfigDataLoaded) return;
            setEditingCard(null);
          }}
          onUpdate={handleGeneralConfigUpdate}
          errors={Object.fromEntries(
            Object.entries(validationState.general || {}).map(([key, error]) => [
              key,
              error.message,
            ])
          )}
          hints={hintsState.general || {}}
          sensorCount={sensorCount}
          showSecondSpanCal={hasActiveCo2Sensor}
        />
        {renderSensorCards()}
      </GridComponent>
    );
  };

  // ===== 렌더링 =====

  return (
    <ConfigPageShell
      deviceType={DeviceType.LITE}
      detectedDeviceType={detectedDeviceType}
      expectedDeviceType={EXPECTED_DEVICE_TYPE}
      isDeviceConnected={isDeviceConnected}
      isCompatible={isCompatible}
      onDeviceLoaded={onDeviceLoaded}
      onConnectionChange={onConnectionChange}
      onDisconnected={onDisconnected}
      onSaveToStorage={handleSaveToStorage}
      onLoadFromStorage={handleLoadFromStorage}
      onLoadDefault={loadDefaultConfiguration}
      onSave={handleSave}
      canSaveOrLoad={canSaveOrLoad}
      canLoadFromStorage={isDeviceConnected && isCompatible && !!curDevice?.serial_number}
      configListModalVisible={showConfigListModal}
      onConfigSelect={handleConfigSelect}
      onConfigListClose={() => setShowConfigListModal(false)}
      nameInputModalVisible={showNameInputModal}
      onNameConfirm={handleSaveWithName}
      onNameInputClose={() => setShowNameInputModal(false)}
    >
      {renderSensorGrid()}
    </ConfigPageShell>
  );
};

export default ConfigurationLite;
